/** design.md §P3 — Notifications. */

import { useMutation, useQuery } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { RelativeTime } from '@/components/bits'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/overlay'
import { api } from '@/lib/api'
import type { Notification, NotificationKind, Page } from '@/lib/types'
import { cn } from '@/lib/utils'

const ACCENT: Record<NotificationKind, string> = {
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warn',
  error: 'bg-danger',
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api.get<{ unread: number }>('/api/notifications/unread-count'),
    // design.md §P3: poll on an interval and refetch on focus. No websocket in
    // Phase 1 -- the count is a single indexed aggregate.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

export function NotificationsBell() {
  const navigate = useNavigate()
  const unread = useUnreadCount()

  const list = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => api.get<Page<Notification>>('/api/notifications', { page_size: 10 }),
    enabled: false,
  })

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/api/notifications/${id}/read`),
  })

  const markAll = useMutation({
    mutationFn: () => api.post('/api/notifications/read-all'),
  })

  const count = unread.data?.unread ?? 0
  const items = list.data?.items ?? []

  function open(notification: Notification) {
    // Clicking marks read and follows the link. Opening the popover does not
    // mark anything -- that is an explicit action (design.md §P3).
    if (!notification.read_at) markRead.mutate(notification.id)
    if (notification.link) navigate(notification.link)
  }

  return (
    <Popover
      onOpenChange={(isOpen) => {
        if (isOpen) void list.refetch()
      }}
    >
      <PopoverTrigger asChild>
        <button
          className="relative flex size-9 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-text"
          aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        >
          <Bell className="size-[18px]" />
          {count > 0 ? (
            <span className="tabular absolute top-1 right-1 min-w-4 rounded-full bg-accent px-1 text-[10px] leading-4 font-semibold text-accent-fg">
              {count > 9 ? '9+' : count}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-[22rem] p-0">
        <div className="flex items-center justify-between border-b border-border-soft px-3 py-2">
          <p className="text-[13px] font-semibold">Notifications</p>
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => markAll.mutate()}
            disabled={count === 0 || markAll.isPending}
          >
            Mark all read
          </Button>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {list.isFetching && items.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-faint">Loading…</p>
          ) : items.length === 0 ? (
            <p className="px-3 py-8 text-center text-xs text-faint">
              Nothing yet. Alerts about your work will appear here.
            </p>
          ) : (
            <ul>
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => open(item)}
                    className="flex w-full gap-2.5 border-b border-border-soft px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-surface-2"
                  >
                    <span
                      className={cn(
                        'mt-1.5 size-1.5 shrink-0 rounded-full',
                        item.read_at ? 'bg-transparent' : ACCENT[item.kind],
                      )}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span
                          className={cn(
                            'truncate text-[13px]',
                            item.read_at ? 'text-muted' : 'font-medium text-text',
                          )}
                        >
                          {item.title}
                        </span>
                        <span className="shrink-0 text-[11px]">
                          <RelativeTime value={item.added_at} />
                        </span>
                      </span>
                      {item.body ? (
                        <span className="mt-0.5 line-clamp-2 block text-xs text-muted">
                          {item.body}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-border-soft p-1">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center text-xs"
            onClick={() => navigate('/notifications')}
          >
            See all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
